import React, {useEffect, useRef} from "react";
import {useCurrentShow, useCurrentShowId} from "hooks";
import {useEmbeddedChatStore} from "store/embedded-chat/embedded-chat.store";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {Roles, ShowExhibitorChatMode} from "@evennt-tech/api-v2-core";
import {runEmbeddedChatProviderCleanup} from "tools/3rdParty/embeddedChatCleanup";

// MUI modal/drawer renders at z-index 1300. Cap all known embedded chat widget
// containers to 1290 so drawers always appear on top.
const CHAT_WIDGET_ZINDEX_CSS = `
  #hubspot-messages-iframe-container,
  .intercom-lightweight-app,
  #intercom-container,
  #zsiqwidget,
  #zohohc-asap-web-viewer,
  #chat-widget-container,
  #fc_widget,
  #fc_frame,
  #freshworks-container,
  #freshworks-frame,
  #embedded-messaging,
  embedded-service-help-button,
  #LeadboosterContainer,
  .LeadBooster,
  .LeadBoosterContainer,
  [id^="LeadBooster"],
  [class*="leadbooster"],
  [data-pd-webforms] {
    z-index: 1290 !important;
  }
  body .zls-sptwndw,
  body .zsiq_floatmain,
  body .zls-btnmn,
  .intercom-namespace,
  [class*="intercom-with-namespace"],
  [class^="zima"] {
    z-index: 1290 !important;
}
`;

const VALID_ROLES = [Roles.EXHIBITOR, Roles.ORGANISER];

const EmbeddedChat = () => {
  const [show] = useCurrentShow();
  const {role} = useSelector(selectUser);
  const showId = show ? show.id : null;
  const paramShowId = useCurrentShowId();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    fetchEmbeddedChat,
    autoCatchEmbeddedChat,
    reset,
    embeddedChat,
    isTestMode,
    isLoading,
  } = useEmbeddedChatStore((state) => ({
    fetchEmbeddedChat: state.fetch,
    autoCatchEmbeddedChat: state.autoCatch,
    isTestMode: state.isTestMode,
    embeddedChat: state.embeddedChat,
    reset: state.reset,
    isLoading: state.isLoading,
  }));

  const prevCodeSnippet = useRef<string>(embeddedChat?.codeSnippet || "");
  const prevIsLoading = useRef(isLoading);
  const prevExhibitorChatMode = useRef<ShowExhibitorChatMode | undefined>(
    undefined
  );
  const chatModeCheckedForShow = useRef<string | null>(null);

  useEffect(() => {
    if (role !== Roles.EXHIBITOR || !embeddedChat?.codeSnippet) return;

    const style = document.createElement("style");
    style.textContent = CHAT_WIDGET_ZINDEX_CSS;
    document.head.appendChild(style);

    const widgetObservers: MutationObserver[] = [];

    const clampIfHigh = (node: HTMLElement) => {
      if (!node.id || !CHAT_WIDGET_ZINDEX_CSS.includes(node.id)) return;
      const z = parseInt(getComputedStyle(node).zIndex, 10);
      if (!isNaN(z) && z > 1290) {
        node.style.setProperty("z-index", "1290", "important");
      }
    };

    const watchBodyChild = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;
      if (Object.keys(node).some((k) => k.startsWith("__reactFiber"))) return;

      clampIfHigh(node);
      const obs = new MutationObserver(() => clampIfHigh(node));
      obs.observe(node, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      widgetObservers.push(obs);
    };

    Array.from(document.body.children).forEach(watchBodyChild);
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach(watchBodyChild));
    });
    bodyObserver.observe(document.body, {childList: true});

    return () => {
      style.remove();
      bodyObserver.disconnect();
      widgetObservers.forEach((obs) => obs.disconnect());
    };
  }, [role, embeddedChat?.codeSnippet]);

  useEffect(() => {
    const isValidRole = role && VALID_ROLES.includes(role);
    if (!showId || !isValidRole) return;

    fetchEmbeddedChat(showId, true).catch();
    let unsub: () => void;
    if (role === Roles.ORGANISER) {
      unsub = autoCatchEmbeddedChat(showId);
    }
    return () => {
      if (unsub) {
        unsub();
      }
      reset();
    };
  }, [showId]);

  useEffect(() => {
    const loadingCompleted = prevIsLoading.current && !isLoading;
    prevIsLoading.current = isLoading;

    if (!loadingCompleted || role !== Roles.EXHIBITOR || !showId) return;

    const prevCode = prevCodeSnippet.current ?? "";
    const newCode = embeddedChat?.codeSnippet ?? "";

    if (prevCode && prevCode !== newCode) {
      window.location.reload();
    }
  }, [isLoading, showId]);
  useEffect(() => {
    if (!showId || !show || role !== Roles.EXHIBITOR) return;
    if (chatModeCheckedForShow.current === showId) return;

    chatModeCheckedForShow.current = showId;

    const curr = show.exhibitorChatMode;
    const prev = prevExhibitorChatMode.current;
    prevExhibitorChatMode.current = curr;

    if (prev === undefined || prev === curr) return;

    if (
      prev === ShowExhibitorChatMode.Embedded ||
      curr === ShowExhibitorChatMode.Embedded
    ) {
      window.location.reload();
    }
  }, [show?.exhibitorChatMode, showId, role]);

  useEffect(() => {
    if (!paramShowId) {
      chatModeCheckedForShow.current = null;
    }
  }, [paramShowId]);

  const shouldRenderChat =
    (role === Roles.ORGANISER && isTestMode) ||
    (role === Roles.EXHIBITOR &&
      show?.exhibitorChatMode === ShowExhibitorChatMode.Embedded);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!embeddedChat?.codeSnippet || !shouldRenderChat || !containerEl) return;

    const bodySnapshot = new Set<Node>(Array.from(document.body.children));

    const doc = new DOMParser().parseFromString(
      `<!doctype html><html lang="en"><body>${embeddedChat.codeSnippet}</body></html>`,
      "text/html"
    );

    Array.from(doc.body.childNodes).forEach((node) => {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).tagName === "SCRIPT"
      ) {
        const el = document.createElement("script");
        Array.from((node as Element).attributes).forEach((attr) => {
          el.setAttribute(attr.name, attr.value);
        });
        if (node.textContent) el.textContent = node.textContent;
        containerEl.appendChild(el);
      } else {
        containerEl.appendChild(document.importNode(node, true));
      }
    });

    prevCodeSnippet.current = embeddedChat.codeSnippet;

    return () => {
      runEmbeddedChatProviderCleanup();
      containerEl.innerHTML = "";

      if (role !== Roles.ORGANISER) {
        return;
      }

      Array.from(document.body.children).forEach((node) => {
        if (
          !bodySnapshot.has(node) &&
          !Object.keys(node).some((k) => k.startsWith("__reactFiber"))
        ) {
          node.remove();
        }
      });
    };
  }, [embeddedChat?.codeSnippet, shouldRenderChat, role]);

  return <div ref={containerRef} />;
};

export default EmbeddedChat;
